import errorReporter from '@app/errorReporter';
import { NonAgentJsonType } from '@bb/domain/dist/types';
import { CustomAgentJsonType } from '@bb/domain/dist/types/CustomAgent';
import { TrrebApiReason } from '@bb/users';
import { message } from 'components';
import { AuthContext } from 'contexts';
import React, { useContext } from 'react';
import type { IUserSearchDetailsResponse } from '@/app-package';
import getCustomAgent from '../../../../../api/CustomAgentController/get';
import type { NonAgentSearchUser } from '../../../../../api/NonAgentController/search';
import getNonAgent from '../../../../../api/NonAgentController/searchDetails';
import { searchDetails } from '../../../../../api/UserController';
import AppointmentBookingInternalContext from '../../AppointmentBookingInternalContext';
import StepPanel from '../../StepPanel';
import BookForButtons from './BookForButtons';
import CoopSelectContext from './CoopSelectContext';
import CustomAgentCreate, {
  type CustomAgentCreateProps,
} from './CustomAgentCreate';
import InPersonCoopDetails from './InPersonCoopDetails';
import InPersonNonAgentInput from './InPersonNonAgentInput';
import InPersonRosterInput, { AgentUserResult } from './InPersonRosterInput';
import NonAgentCreate, { type NonAgentCreateProps } from './NonAgentCreate';

const CoopSelectorStep: React.FC = () => {
  const { auth } = useContext(AuthContext);
  const { listing, hasElevatedAccess } = useContext(
    AppointmentBookingInternalContext,
  );
  const { setApptType, bookFor, setBookFor, setCoop, setCoopAdditionalData } =
    useContext(CoopSelectContext);

  let title = 'Select Agent';
  if (bookFor === 'self') {
    title = 'Your Profile';
  } else if (bookFor === 'nonAgent' || bookFor === 'nonAgentCreate') {
    title = 'Select Profile';
  }

  const handleSelectRosterAgent = async (user: AgentUserResult) => {
    try {
      const foundUser: IUserSearchDetailsResponse & {
        uid?: string;
        mlsType?: string;
      } = await searchDetails({
        ...user,
        reason: TrrebApiReason.ViewingPreSale,
        listingId: listing!.id,
      });
      foundUser.uid ||= user.uid;
      foundUser.mlsType ||= user.mlsType;
      setCoop({
        uid: foundUser.uid,
        ...('id' in foundUser && { id: foundUser.id }),
        email: foundUser.emails?.[0] || foundUser.email || '',
        name: `${foundUser.firstName} ${foundUser.lastName}`,
        firstName: foundUser.firstName,
        lastName: foundUser.lastName,
        board: foundUser.board || '',
        boardId: foundUser.boardId,
        mls: foundUser.mlsType,
        phone: foundUser.lastPhone || (user.id ? foundUser.phone || '' : ''),
        // phone: foundUser[user.id ? 'phone' : 'lastPhone'] || '',
        org: {
          address: foundUser.org.address,
          name: foundUser.org.name || '',
          phone: foundUser.org.phone || '',
        },
      });
      setCoopAdditionalData({
        coopType: 'agent',
        orgAddress: foundUser.org.address,
        orgName: foundUser.org.name || '',
        orgPhone: foundUser.org.phone || '',
        ...('id' in foundUser && {
          orgFeatures: foundUser.org.features,
          notifications: foundUser.notifications,
        }),
      });
    } catch (err) {
      errorReporter.error(err);
      message.error('Error selecting agent');
    }
  };

  const setCustomAgentCoop = (customAgent: CustomAgentJsonType) => {
    setCoop({
      id: customAgent.id,
      firstName: customAgent.firstName,
      lastName: customAgent.lastName,
      name: `${customAgent.firstName} ${customAgent.lastName}`,
      email: customAgent.email,
      phone: customAgent.phone || '',
    });
    setCoopAdditionalData({
      coopType: 'customAgent',
      orgAddress: customAgent.companyAddress || '',
      orgName: customAgent.companyName,
      orgPhone: customAgent.companyPhone,
      createdBy: customAgent.createdBy,
    });
  };

  const setNonAgentCoop = (nonAgent: NonAgentJsonType) => {
    setApptType(nonAgent.type);
    setCoop({
      id: nonAgent.id,
      firstName: nonAgent.firstName,
      lastName: nonAgent.lastName,
      name: `${nonAgent.firstName} ${nonAgent.lastName}`,
      email: nonAgent.email,
      phone: nonAgent.phone || '',
    });
    setCoopAdditionalData({
      coopType: 'nonAgent',
      orgAddress: nonAgent.companyAddress || '',
      orgName: nonAgent.companyName,
      orgPhone: nonAgent.companyPhone,
      createdBy: nonAgent.createdBy,
      nonAgentType: nonAgent.type,
      customContactPreApproved: Boolean(nonAgent.preApproved),
      isMlsCustomContact: Boolean(nonAgent.onboardingPortalId),
    });
  };

  const handleSelectCustomAgent = async (user: AgentUserResult) => {
    try {
      const customAgent = await getCustomAgent(user.id!);
      setCustomAgentCoop(customAgent);
    } catch (err) {
      errorReporter.error(err);
      message.error('Error selecting custom agent');
    }
  };

  const handleSelectNonAgent = async (user: NonAgentSearchUser) => {
    try {
      const nonAgent = await getNonAgent(user.id);
      setNonAgentCoop(nonAgent);
    } catch (err) {
      errorReporter.error(err);
      message.error('Error selecting custom contact');
    }
  };

  const onCustomAgentSubmit: CustomAgentCreateProps['onCustomAgentSubmit'] =
    customAgent => {
      setBookFor('otherAgent');
      setCustomAgentCoop(customAgent);
    };

  const onNonAgentSubmit: NonAgentCreateProps['onNonAgentSubmit'] =
    nonAgent => {
      setBookFor('nonAgent');
      setNonAgentCoop(nonAgent);
    };

  return (
    <StepPanel title={title}>
      <BookForButtons />
      {bookFor === 'otherAgent' && (
        <InPersonRosterInput
          auth={auth}
          listingId={listing!.id}
          selectUser={handleSelectRosterAgent}
          selectCustomAgent={handleSelectCustomAgent}
          showCustomAgentEnter={hasElevatedAccess}
          onCustomAgentEnter={() => {
            setCoop(null);
            setBookFor('customAgentCreate');
          }}
          deselectUser={() => setCoop(null)}
        />
      )}
      {bookFor === 'nonAgent' && (
        <InPersonNonAgentInput
          selectUser={handleSelectNonAgent}
          deselectUser={() => setCoop(null)}
          showCustomContactEnter={hasElevatedAccess}
          onCustomContactEnter={() => setBookFor('nonAgentCreate')}
        />
      )}
      {bookFor === 'customAgentCreate' && (
        <CustomAgentCreate
          onCustomAgentSubmit={onCustomAgentSubmit}
          onSearchRoster={() => setBookFor('otherAgent')}
        />
      )}
      {bookFor === 'nonAgentCreate' && (
        <NonAgentCreate
          onNonAgentSubmit={onNonAgentSubmit}
          onSearchRoster={() => setBookFor('otherAgent')}
        />
      )}
      {bookFor !== 'customAgentCreate' && bookFor !== 'nonAgentCreate' && (
        <InPersonCoopDetails />
      )}
    </StepPanel>
  );
};

export default CoopSelectorStep;
